import type {
  ObservabilityComputeResourceType,
  ObservabilityEnvironment,
  ObservabilityRuntimeType,
  ObservabilitySeverity,
} from '@scatterin/api-types';
import { differenceInCalendarDays, isAfter, isBefore, startOfDay, subDays } from 'date-fns';
import { parseAsArrayOf, parseAsInteger, parseAsIsoDateTime, parseAsString, parseAsStringLiteral, useQueryStates } from 'nuqs';
import { z } from 'zod';

const maxWindowDays = 31;
const defaultWindowDays = 7;
const minimumWindowHours = 1;
const maxSummaryTopLimit = 50;
const defaultWindowEnd = new Date();
const defaultWindowStart = startOfDay(subDays(defaultWindowEnd, defaultWindowDays));

export const environmentValues = ['dev', 'beta', 'prod'] as const satisfies readonly ObservabilityEnvironment[];
export const runtimeValues = ['lambda', 'ecs', 'browser', 'unknown'] as const satisfies readonly ObservabilityRuntimeType[];
export const queueViewValues = ['open', 'acknowledged', 'resolved', 'ignored', 'all'] as const;
export const failureSeverityValues = ['critical', 'error', 'warning', 'info'] as const satisfies readonly ObservabilitySeverity[];
export const failureRecurrenceValues = ['all', 'recurring', 'single'] as const;
export const failureSortValues = ['priority', 'latest', 'occurrences'] as const;
export const runStatusValues = ['queued', 'starting', 'running', 'completed', 'failed', 'cancelled', 'unknown'] as const;
export const workloadSizeValues = ['small', 'medium', 'large', 'unknown'] as const;
export const rumVitalValues = ['LCP', 'INP', 'CLS', 'FCP', 'FID', 'TTFB', 'page_load', 'navigation'] as const;
export const sessionErrorValues = ['all', 'with', 'without'] as const;
export const logModeValues = ['failure', 'run', 'group'] as const;
export const percentileValues = ['p50', 'p95', 'p99'] as const;
export const resourceTypeValues = [
  'lambda_function',
  'api_route',
  'ecs_task_definition',
  'sqs_queue',
] as const satisfies readonly ObservabilityComputeResourceType[];
export const tabValues = ['summary', 'details', 'raw'] as const;
export const compareMetricValues = [
  'runs',
  'failures',
  'run_duration',
  'startup_duration',
  'queue_wait_duration',
  'span_duration',
  'metric_sample_value',
  'page_load',
  'route_navigation',
  'web_vital_value',
  'client_errors',
] as const;
export const compareDimensionValues = [
  'analysis_type',
  'service_name',
  'runtime_type',
  'source',
  'job_kind',
  'status',
  'span_name',
  'metric_name',
  'metric_family',
  'route',
  'web_vital_name',
] as const;

const listParser = parseAsArrayOf(parseAsString).withDefault([]);

const urlStateParsers = {
  env: parseAsStringLiteral(environmentValues).withDefault('dev'),
  start: parseAsIsoDateTime.withDefault(defaultWindowStart),
  end: parseAsIsoDateTime.withDefault(defaultWindowEnd),
  analysisTypes: listParser,
  serviceNames: listParser,
  runtimeTypes: parseAsArrayOf(parseAsStringLiteral(runtimeValues)).withDefault([]),
  sources: listParser,
  search: parseAsString.withDefault(''),
  failureGroupKeys: listParser,
  failureSeverities: parseAsArrayOf(parseAsStringLiteral(failureSeverityValues)).withDefault([]),
  failureRecurrence: parseAsStringLiteral(failureRecurrenceValues).withDefault('all'),
  failureSort: parseAsStringLiteral(failureSortValues).withDefault('priority'),
  runStatuses: parseAsArrayOf(parseAsStringLiteral(runStatusValues)).withDefault([]),
  workloadSizeClasses: parseAsArrayOf(parseAsStringLiteral(workloadSizeValues)).withDefault([]),
  workerImageVersions: listParser,
  runOffset: parseAsInteger.withDefault(0),
  runPageSize: parseAsInteger.withDefault(25),
  sessionRoutes: listParser,
  sessionBrowsers: listParser,
  sessionDevices: listParser,
  sessionVitalNames: parseAsArrayOf(parseAsStringLiteral(rumVitalValues)).withDefault([]),
  sessionErrorStatus: parseAsStringLiteral(sessionErrorValues).withDefault('all'),
  sessionOffset: parseAsInteger.withDefault(0),
  sessionPageSize: parseAsInteger.withDefault(25),
  resourceTypes: parseAsArrayOf(parseAsStringLiteral(resourceTypeValues)).withDefault([]),
  resourceOffset: parseAsInteger.withDefault(0),
  resourcePageSize: parseAsInteger.withDefault(50),
  resourceKey: parseAsString,
  resourceType: parseAsStringLiteral(resourceTypeValues),
  topLimit: parseAsInteger.withDefault(10),
  queueLimit: parseAsInteger.withDefault(50),
  queueOffset: parseAsInteger.withDefault(0),
  rawFactLimit: parseAsInteger.withDefault(25),
  selectedTab: parseAsStringLiteral(tabValues).withDefault('summary'),
  percentile: parseAsStringLiteral(percentileValues).withDefault('p95'),
  queueView: parseAsStringLiteral(queueViewValues).withDefault('open'),
  compareMetric: parseAsStringLiteral(compareMetricValues).withDefault('run_duration'),
  compareDimension: parseAsStringLiteral(compareDimensionValues).withDefault('analysis_type'),
  logMode: parseAsStringLiteral(logModeValues).withDefault('failure'),
  runId: parseAsString,
  sessionId: parseAsString,
  failureId: parseAsString,
  logGroup: parseAsString,
  logStream: parseAsString,
  logNextToken: parseAsString,
  userPageToken: parseAsString,
};

const urlStateSchema = z.object({
  env: z.enum(environmentValues),
  start: z.date(),
  end: z.date(),
  analysisTypes: z.array(z.string()),
  serviceNames: z.array(z.string()),
  runtimeTypes: z.array(z.enum(runtimeValues)),
  sources: z.array(z.string()),
  search: z.string(),
  failureGroupKeys: z.array(z.string()),
  failureSeverities: z.array(z.enum(failureSeverityValues)),
  failureRecurrence: z.enum(failureRecurrenceValues),
  failureSort: z.enum(failureSortValues),
  runStatuses: z.array(z.enum(runStatusValues)),
  workloadSizeClasses: z.array(z.enum(workloadSizeValues)),
  workerImageVersions: z.array(z.string()),
  runOffset: z.number().int().min(0).max(10_000),
  runPageSize: z.number().int().min(1).max(100),
  sessionRoutes: z.array(z.string()),
  sessionBrowsers: z.array(z.string()),
  sessionDevices: z.array(z.string()),
  sessionVitalNames: z.array(z.enum(rumVitalValues)),
  sessionErrorStatus: z.enum(sessionErrorValues),
  sessionOffset: z.number().int().min(0).max(10_000),
  sessionPageSize: z.number().int().min(1).max(100),
  resourceTypes: z.array(z.enum(resourceTypeValues)),
  resourceOffset: z.number().int().min(0).max(10_000),
  resourcePageSize: z.number().int().min(1).max(250),
  resourceKey: z.string().nullable(),
  resourceType: z.enum(resourceTypeValues).nullable(),
  topLimit: z.number().int().min(1).max(maxSummaryTopLimit),
  queueLimit: z.number().int().min(1).max(200),
  queueOffset: z.number().int().min(0).max(10_000),
  rawFactLimit: z.number().int().min(1).max(100),
  selectedTab: z.enum(tabValues),
  percentile: z.enum(percentileValues),
  queueView: z.enum(queueViewValues),
  compareMetric: z.enum(compareMetricValues),
  compareDimension: z.enum(compareDimensionValues),
  logMode: z.enum(logModeValues),
  runId: z.string().nullable(),
  sessionId: z.string().nullable(),
  failureId: z.string().nullable(),
  logGroup: z.string().nullable(),
  logStream: z.string().nullable(),
  logNextToken: z.string().nullable(),
  userPageToken: z.string().nullable(),
});

export type AnalyticsUrlState = z.infer<typeof urlStateSchema>;

export const normalizeWindow = (start: Date, end: Date): { start: Date; end: Date } => {
  const now = new Date();
  let normalizedEnd = isAfter(end, now) ? now : end;
  let normalizedStart = start;

  if (!isBefore(normalizedStart, normalizedEnd)) {
    normalizedStart = new Date(normalizedEnd.getTime() - minimumWindowHours * 60 * 60 * 1000);
  }

  if (differenceInCalendarDays(normalizedEnd, normalizedStart) > maxWindowDays) {
    normalizedStart = subDays(normalizedEnd, maxWindowDays);
  }

  if (isAfter(normalizedStart, now)) {
    normalizedStart = startOfDay(subDays(now, defaultWindowDays));
    normalizedEnd = now;
  }

  return {
    end: normalizedEnd,
    start: normalizedStart,
  };
};

export const useAnalyticsUrlState = () => {
  const [state, setState] = useQueryStates(urlStateParsers, { history: 'replace' });
  const normalizedWindow = normalizeWindow(state.start, state.end);
  const parsed = urlStateSchema.parse({
    ...state,
    end: normalizedWindow.end,
    start: normalizedWindow.start,
    topLimit: Math.min(Math.max(state.topLimit, 1), maxSummaryTopLimit),
    queueLimit: Math.min(Math.max(state.queueLimit, 1), 200),
    queueOffset: Math.min(Math.max(state.queueOffset, 0), 10_000),
    rawFactLimit: Math.min(Math.max(state.rawFactLimit, 1), 100),
    runOffset: Math.min(Math.max(state.runOffset, 0), 10_000),
    runPageSize: Math.min(Math.max(state.runPageSize, 1), 100),
    sessionOffset: Math.min(Math.max(state.sessionOffset, 0), 10_000),
    sessionPageSize: Math.min(Math.max(state.sessionPageSize, 1), 100),
    resourceOffset: Math.min(Math.max(state.resourceOffset, 0), 10_000),
    resourcePageSize: Math.min(Math.max(state.resourcePageSize, 1), 250),
  });

  return [parsed, setState] as const;
};

export const serializeDate = (date: Date): string => date.toISOString();
